import { Injectable } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { logFeature } from '@office-start/telemetry-service';
import { KnownFeatureInstrumentationEvents, KnownFeatureNames } from '@office-start/telemetry-types';

import { ApplicationError } from '../../error-handler/error-handler.service';
import { Performance } from '../services/performance.service';
import { UnloadEventService } from '../services/unload-event.service';
import { ContextService } from '../shared-context/context.service';
import { GuidGenerator } from '../utilities/guid-generator.utility';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from './instrumentation.service';

export interface ActivitySessionsStats {
    activitySessionsCount: number;
    cumulativeClickCount: number;
    cumulativeDurationBetweenActivitySessions: number;
    cumulativeDurationOfActivitySessions: number;
    inactivityStartTime: number;
}

@Injectable({
    providedIn: 'root'
})
export class ActivitySessionService {

    private instrumentationService: InstrumentationService;
    private currentSessionIndex = 0;
    private currentSessionId: string = null;
    private currentSessionStartTime: number = 0;
    private currentSessionClickCount: number = 0;
    private inactivityStartTime: number = 0;
    private inactivityTrackerTimeout: ReturnType<typeof setTimeout>;
    private inactivityTrackerStartTime: number = 0;
    private cumulativeDurationOfActivitySessions: number = 0;
    private cumulativeDurationBetweenActivitySessions: number = 0;
    private cumulativeClickCount: number = 0;

    private mousedownListener: Function;
    private mousemoveListener: Function;
    private keypressListener: Function;

    constructor(
        private readonly contextService: ContextService,
        private readonly unloadEventService: UnloadEventService,
        private readonly eventManager: EventManager
    ) {
    }

    public initialize(instrumentationService: InstrumentationService) {

        if (!this.contextService.sharedContext.activityTrackingEnabled) {
            return;
        }

        this.instrumentationService = instrumentationService;

        // Setup handlers for events indicating activity on the page
        const activityDetected = () => {
            this.startSession();
            this.trackInactivity();
        };

        activityDetected();

        this.mousedownListener = this.eventManager.addGlobalEventListener('document', 'mousedown', activityDetected);
        this.mousemoveListener = this.eventManager.addGlobalEventListener('document', 'mousemove', activityDetected);
        this.keypressListener = this.eventManager.addGlobalEventListener('document', 'keypress', activityDetected);

        this.unloadEventService.addUnloadEvent(() => {
            if (this.mousedownListener) {
                this.mousedownListener();
            }
            if (this.mousemoveListener) {
                this.mousemoveListener();
            }
            if (this.keypressListener) {
                this.keypressListener();
            }
            clearTimeout(this.inactivityTrackerTimeout);
            this.endSession(this.inactivityTrackerStartTime ? Performance.now() - this.inactivityTrackerStartTime : 0);
        });
    }

    public startSession(): string {

        if (!this.contextService.sharedContext.activityTrackingEnabled) {
            return null;
        }

        if (this.currentSessionId) {
            // Session is already underway
            return this.currentSessionId;
        }

        this.currentSessionStartTime = Performance.now();
        this.currentSessionId = GuidGenerator.createGuid();
        this.currentSessionIndex++;
        this.currentSessionClickCount = 0;

        const params = {};
        params[InstrumentationParam.ActivitySessionId] = this.currentSessionId;
        params[InstrumentationParam.Index] = this.currentSessionIndex.toString();
        if (this.currentSessionIndex > 1) {
            if (this.inactivityStartTime <= 0) {
                throw new ApplicationError('Logical error in ActivitySessionService: inactivityStartTime must be set to valid value when startSession() is called');
            }
            const inactivityDuration = this.currentSessionStartTime - this.inactivityStartTime;
            this.cumulativeDurationBetweenActivitySessions += inactivityDuration;
            params[InstrumentationParam.Latency] = Math.ceil(inactivityDuration).toString();
        }

        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.ActivitySessionStarted, params);
        logFeature({ featureName: KnownFeatureNames.ActivitySession, eventName: KnownFeatureInstrumentationEvents.ActivitySessionStarted, params});

        return this.currentSessionId;
    }

    public endSession(elapsedInactivityWithinSession: number = 0): ActivitySessionsStats {

        if (!this.contextService.sharedContext.activityTrackingEnabled) {
            return null;
        }

        if (!this.currentSessionId) {
            // Session has not started yet
            return this.getCurrentStats();
        }

        this.inactivityStartTime = Performance.now() - elapsedInactivityWithinSession;

        const params = {};
        params[InstrumentationParam.ActivitySessionId] = this.currentSessionId;
        params[InstrumentationParam.Index] = this.currentSessionIndex.toString();
        params[InstrumentationParam.ClickCount] = this.currentSessionClickCount.toString();
        const activityDuration = this.inactivityStartTime - this.currentSessionStartTime;
        this.cumulativeDurationOfActivitySessions += activityDuration;
        params[InstrumentationParam.Latency] = Math.ceil(activityDuration).toString();

        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.ActivitySessionFinished, params);
        logFeature({ featureName: KnownFeatureNames.ActivitySession, eventName: KnownFeatureInstrumentationEvents.ActivitySessionFinished, params});

        // Reset session id that is used to determine if there is an activity session underway
        this.currentSessionId = null;

        return this.getCurrentStats();
    }

    public incrementClick() {
        this.startSession();
        this.currentSessionClickCount++;
        this.cumulativeClickCount++;
    }

    private getCurrentStats(): ActivitySessionsStats {
        return {
            activitySessionsCount: this.currentSessionIndex,
            cumulativeClickCount: this.cumulativeClickCount,
            cumulativeDurationOfActivitySessions: this.cumulativeDurationOfActivitySessions,
            cumulativeDurationBetweenActivitySessions: this.cumulativeDurationBetweenActivitySessions,
            inactivityStartTime: this.inactivityStartTime
        };
    }

    private trackInactivity() {
        clearTimeout(this.inactivityTrackerTimeout);
        this.inactivityTrackerStartTime = Performance.now();
        this.inactivityTrackerTimeout = setTimeout(
            () => this.endSession(Performance.now() - this.inactivityTrackerStartTime),
            this.contextService.sharedContext.inactivityTimeoutInSeconds * 1000);
    }
}
